import { ExternalLinkIcon, MailOpenIcon } from '@heroicons/react/outline';
import { CheckIcon, MailIcon, PaperClipIcon, StarIcon as SolidStarIcon } from '@heroicons/react/solid';
import * as React from 'react';
import * as NotificationApi from '@mam/main/Api/Pigeon';
import { groupBy } from '@mam/main/Collections';
import { formatISODate, formatNumericUserDate, formatTime, formatUserDateWithWeekday } from '@mam/main/Date';
import { useToggle } from '@mam/main/Hooks';
import { Tooltip } from '@mam/main/UI';
import { Card } from '@mam/main/UI/Card';

export const Feed: React.FunctionComponent<{
	notifications: NotificationApi.Notification[];
	status: string;
	onSetPriority(notificationId: string, priority: boolean): void;
	onSetRead(notificationId: string): void;
	onSetReadAll(notificationId: string[]): void;
}> = ({ notifications, status, onSetPriority, onSetRead, onSetReadAll }) => (
	<div className='mt-10 px-4'>
		<div className='px-4 mb-10'>
			{notifications.length === 0 ? <p className='p-4 text-base'>You have no notifications</p> : null}
			{Array.from(groupByDays(notifications).entries()).map((value) => {
				const [, items] = value;
				return (
					<Day
						day={items[0].createdTime}
						key={formatISODate(items[0].createdTime)}
						notifications={items}
						status={status}
						onSetPriority={onSetPriority}
						onSetRead={onSetRead}
						onSetReadAll={onSetReadAll}
					/>
				);
			})}
		</div>
	</div>
);

Feed.displayName = 'NotificationList.Feed';

export const Day: React.FunctionComponent<{
	day: Date;
	notifications: NotificationApi.Notification[];
	status: string;
	onSetPriority(notificationId: string, priority: boolean): void;
	onSetRead(notificationId: string): void;
	onSetReadAll(notificationId: string[]): void;
}> = ({ day, notifications, onSetPriority, onSetRead, onSetReadAll }) => (
	<div className='mb-10' key={formatISODate(day)}>
		<Card>
			<Header
				day={day}
				onSetReadAll={() => {
					onSetReadAll(notifications.map((notification) => notification.id));
				}}
			/>
			<List notifications={notifications} onSetPriority={onSetPriority} onSetRead={onSetRead} />
		</Card>
	</div>
);

const Header: React.FunctionComponent<{
	day: Date;
	onSetReadAll(): void;
}> = ({ day, onSetReadAll }) => (
	<div className='flex flex-row justify-between flex-wrap w-full h-16 py-4 pl-5 pr-2 sticky top-0 bg-white border-b border-cogvio-100'>
		<h2 className='text-gray-500 text-xl font-thin'>{formatUserDateWithWeekday(day, ['en'])}</h2>
		<div className='flex flex-row items-center gap-5'>
			<Tooltip title='Mark all the messages as read at a given day' width='w-48'>
				<CheckIcon
					aria-hidden='true'
					className='p-1 flex-shrink-0 inline h-7 w-7 text-gray-500 cursor-pointer stroke-2 rounded-full hover:bg-green-700 hover:text-white'
					onClick={onSetReadAll}
				/>
			</Tooltip>
		</div>
	</div>
);

Header.displayName = 'NotificationList.Header';

const List: React.FunctionComponent<{
	notifications: NotificationApi.Notification[];
	onSetPriority(notificationId: string, priority: boolean): void;
	onSetRead(notificationId: string): void;
}> = ({ notifications, onSetPriority, onSetRead }) => (
	<div className='divide-y divide-gray-200 rounded-md'>
		{notifications.map((notification) => (
			<Row key={notification.id} notification={notification} onSetPriority={onSetPriority} onSetRead={onSetRead} />
		))}
	</div>
);

List.displayName = 'NotificationList.List';

const Row: React.FunctionComponent<{
	notification: NotificationApi.Notification;
	onSetPriority(notificationId: string, priority: boolean): void;
	onSetRead(notificationId: string): void;
}> = ({ notification, onSetPriority, onSetRead }) => {
	const [expanded, toggleExpanded] = useToggle();

	const { email } = notification;

	return (
		<div className='flex flex-col group'>
			<div className={`flex flex-row group-hover:bg-gray-50`}>
				<div className='py-2 pl-4 flex-shrink-0'>
					{notification.authenticatedRecipient.isRead ? (
						<MailOpenIcon aria-hidden='true' className='flex-shrink-0 inline h-9 w-9 p-2 text-cogvio-300' />
					) : (
						<MailIcon
							aria-hidden='true'
							className='flex-shrink-0 inline h-9 w-9 p-2 text-cogvio-300'
							onClick={() => {
								onSetRead(notification.id);
							}}
						/>
					)}
					{notification.authenticatedRecipient.priority === NotificationApi.NotificationPriority.High ? (
						<SolidStarIcon
							aria-hidden='true'
							className='flex-shrink-0 inline h-9 w-9 p-2 mr-1 text-yellow-400 rounded-full hover:bg-yellow-200 cursor-pointer'
							onClick={() => {
								onSetPriority(notification.id, false);
							}}
						/>
					) : (
						<SolidStarIcon
							aria-hidden='true'
							className='flex-shrink-0 inline h-9 w-9 p-2 mr-1 text-gray-200 rounded-full hover:bg-yellow-200 cursor-pointer'
							onClick={() => {
								onSetPriority(notification.id, true);
							}}
						/>
					)}
				</div>
				<a
					className='flex flex-row flex-grow justify-between py-4 pr-4 text-sm cursor-pointer md:overflow-hidden'
					onClick={() => {
						toggleExpanded();
						if (!notification.authenticatedRecipient.isRead) {
							onSetRead(notification.id);
						}
					}}
				>
					<div className='md:truncate pr-2'>
						<span className={`${notification.authenticatedRecipient.isRead ? '' : 'font-semibold'}`}>
							{notification.email?.subject ?? 'Notification'}
						</span>
						{!expanded ? (
							<span className='font-thin text-gray-600 pl-2 text-xs'>
								{notification.perex !== null ? `${notification.perex.substr(0, 500)}…` : null}
							</span>
						) : null}
					</div>
					<span className='block text-gray-500'>
						{(notification.email?.attachments.length ?? 0) > 0 ? (
							<PaperClipIcon aria-hidden='true' className='inline flex-shrink-0 h-4 w-4 mr-2 text-gray-400' />
						) : null}
						{formatTime(notification.createdTime, ['en'])}
					</span>
				</a>
			</div>
			{expanded ? (
				<div className={`flex flex-row pl-7 pb-3 group-hover:bg-gray-50`}>
					{email !== null ? (
						<div className='w-full'>
							<dl>
								<DetailRow title='Sender'>
									<InternetAddressHandle address={email.from} />
								</DetailRow>
								<DetailRow title='Receiver'>
									{email.to.map((recipient, id) => (
										<InternetAddressHandle address={recipient} key={id} />
									))}
								</DetailRow>
								{email.cc.length > 0 ? (
									<DetailRow title='Cc'>
										{email.cc.map((recipient, id) => (
											<InternetAddressHandle address={recipient} key={id} />
										))}
									</DetailRow>
								) : null}
								{notification.perex !== null ? (
									<DetailRow title='Content'>
										<div className='text-xs pb-4'>{`${notification.perex.substr(0, 500)}…`}</div>
									</DetailRow>
								) : null}
								{email.attachments.length > 0 ? (
									<DetailRow title='Attachments'>
										<ul className='border border-gray-200 rounded-md divide-y divide-gray-200' role='list'>
											{email.attachments.map((attachment) => (
												<li className='pr-4 pb-3 flex items-center justify-between text-sm'>
													<div className='w-0 flex-1 flex items-center'>
														<PaperClipIcon aria-hidden='true' className='flex-shrink-0 h-5 w-5 text-gray-400' />
														<a
															className='ml-2 flex-1 w-0 truncate font-medium text-cogvio-900 hover:text-cogvioBlue-500 hover:underline'
															href={`/notifications/${notification.id}/emails/${email.id}/attachments/${attachment.id}/download`}
															target='_blank'
														>
															{attachment.fileName}
														</a>
													</div>
												</li>
											))}
										</ul>
									</DetailRow>
								) : null}
							</dl>
							<div className='flex flex-row-reverse w-full px-4'>
								<a
									className='mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-cogvio-900 text-white text-base font-medium
									transition duration-200 ease-in-out hover:bg-cogvioBlue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
									href={`/notifications/email/detail?${new URLSearchParams([
										['notificationId', notification.id],
										['emailNotificationId', email.id],
									]).toString()}`}
									target='_blank'
								>
									Show content
									<ExternalLinkIcon className='ml-2 h-5 w-5 text-gray-200' />
								</a>
							</div>
						</div>
					) : null}
				</div>
			) : null}
		</div>
	);
};

Row.displayName = 'NotificationList.Row';

export const DetailRow: React.FunctionComponent<{
	title: string;
}> = ({ children, title }) => (
	<div className='px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
		<dt className='text-sm font-medium text-gray-500'>{title}</dt>
		<dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>{children}</dd>
	</div>
);

DetailRow.displayName = 'Application.Notification.DetailRow';

export const InternetAddressHandle: React.FunctionComponent<{
	address: NotificationApi.InternetAddress;
}> = ({ address }) => (
	<div>{address.personal !== null ? `${address.personal} <${address.emailAddress}>` : `${address.emailAddress}`}</div>
);

InternetAddressHandle.displayName = 'Application.Notification.InternetAddressHandle';

const groupByDays = (notifications: NotificationApi.Notification[]) =>
	groupBy(notifications, (notification) => formatNumericUserDate(notification.createdTime, ['en']));
